/**
 * ACDEV-30609 — publisher accessibility widget (EqualWeb) on the checkout.
 *
 * The widget itself is a third-party script the publisher owns; we only host
 * it per publisher on our CDN and take over where its button is painted. The
 * same CDN layout is used by the webstore (`web-store-platform`
 * `useAccessibility`), so one upload serves both surfaces.
 */
export const ACCESSIBILITY_CDN_BASE_URL = 'https://media.appcharge.com/accessibility';

/** Marks the injected `<script>` so a remount never injects it twice. */
export const ACCESSIBILITY_SCRIPT_PUBLISHER_ATTRIBUTE = 'data-accessibility-publisher';

/** EqualWeb's own ids. The button is either in the document or in the shadow host below. */
export const ACCESSIBILITY_BUTTON_ID = 'INDmenu-btn';
export const ACCESSIBILITY_SHADOW_HOST_ID = 'INDShadowRootHost';

/** EqualWeb paints the button at 80px and scales it through `--indscale`. */
export const ACCESSIBILITY_BUTTON_NATIVE_SIZE_PX = 80;

/** Rendered size of the icon, matching the webstore's floating controls. */
export const ACCESSIBILITY_ICON_SIZE_PX = 44;

/** Smallest gap a *dragged* icon may keep from any viewport edge. */
export const ACCESSIBILITY_EDGE_MARGIN_PX = 12;

/**
 * Checkout card frames the design positions the icon against — not the
 * viewport. Tried in order:
 *   1. `Modal`'s card (`checkout-modal-content`) — full-bleed below `md`,
 *      centred 976x871 above.
 *   2. Preferred mobile swipe-up panel (`checkout-bottom-sheet-panel`) —
 *      that template mounts `CheckoutBottomSheet` instead of `Modal`, so
 *      without this candidate the icon falls back to the viewport and can
 *      sit on the scrim / miss the sheet.
 */
export const ACCESSIBILITY_FRAME_SELECTORS = [
  '[data-testid="checkout-modal-content"]',
  '[data-testid="checkout-bottom-sheet-panel"]',
] as const;

export const ACCESSIBILITY_FRAME_INSET_MOBILE = { left: 12, bottom: 12 } as const;
export const ACCESSIBILITY_FRAME_INSET_DESKTOP = { left: 35, bottom: 27 } as const;

/**
 * Above the checkout modal (`z-50`), below body-level payment chrome
 * (`z-150`: PaymentProcessOverlay, Bizum/Blik/Mbway, and Stripe's body-mounted
 * wallet/3DS UI) and SuccessScreen (`z-200`).
 */
export const ACCESSIBILITY_ICON_Z_INDEX = 100;

/**
 * Pointer travel (px) that turns a tap into a drag. Below it the pointer
 * sequence is left alone so EqualWeb's own click handler opens the menu.
 */
export const ACCESSIBILITY_DRAG_THRESHOLD_PX = 6;

/**
 * EqualWeb's own drag system persists the button's position here, in
 * localStorage, and replays it on the next load. We disable that drag in
 * favour of ours, so any value left under this key is stale.
 */
export const ACCESSIBILITY_EQUALWEB_POSITION_STORAGE_KEY = 'INDbtnPosition';
